import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import { Colors } from '@common/tokens';

export const TextOverflow = ({
  tooltip,
  children,
}: {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const [seeAllText, setSeeAllText] = useState(false);
  const [calculatedWidth, setCalculatedWidth] = useState<number | null>(null);

  useEffect(() => {
    const ref = divRef.current;

    ref && setCalculatedWidth(ref.offsetWidth);
  }, [divRef]);

  const shouldShowFullText = (() => {
    if (!divRef.current || !spanRef.current) {
      return false;
    }

    return spanRef.current.offsetWidth < divRef.current.offsetWidth;
  })();

  const WrappedChildren = (
    <div
      ref={divRef}
      className="text-overflow__container"
      data-testid="container"
    >
      <span
        ref={spanRef}
        className="text-overflow__content"
        data-testid="content"
      >
        {children}
      </span>
      <style jsx>{`
        .text-overflow__container {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  );

  if (shouldShowFullText) {
    return WrappedChildren;
  }

  const shouldShowTooltipWithChildren = tooltip === true;
  if (shouldShowTooltipWithChildren) {
    return <Tooltip title={children}>{WrappedChildren}</Tooltip>;
  }

  if (tooltip) {
    return <Tooltip title={tooltip}>{WrappedChildren}</Tooltip>;
  }

  const ToggleButton = (
    <>
      <button
        className="text-overflow__toggle-button"
        data-testid="toggle-button"
        onClick={() => setSeeAllText(!seeAllText)}
      >
        View {seeAllText ? 'less' : 'more'}
      </button>
      <style jsx>{`
        .text-overflow__toggle-button {
          color: ${Colors.elm};
          padding: 0;
          border: none;
          font-weight: bold;
          background-color: transparent;
          cursor: pointer;
          text-decoration: underline;
        }
      `}</style>
    </>
  );

  return (
    <div>
      {seeAllText ? (
        <>
          {children} {ToggleButton}
        </>
      ) : (
        <>
          <div
            className={`${calculatedWidth ? 'text-overflow__text-toggle' : ''}`}
          >
            {WrappedChildren}
          </div>{' '}
          {ToggleButton}
          <style jsx>{`
            .text-overflow__text-toggle {
              width: calc(${calculatedWidth}px - 100px);
              display: inline-block;
              vertical-align: middle;
            }
          `}</style>
        </>
      )}
    </div>
  );
};
